import _ from 'underscore';
import {v4 as uuidv4} from 'uuid';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {accessibleObjs, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ProcessTaskSchemaEditorQuery} from 'processes/settings/__generated/ProcessTaskSchemaEditorQuery';
import {
  AvailableTaskSchema,
  ClientSchemaMapperConfig,
} from 'processes/settings/graph-builder-flowchart-config/FlowchartConfigConstants';
import {
  IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT,
  IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/redux/reduceFlowchartConfig';

import {ProcessTaskSchemaGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {ProcessInteractiveFlowchart} from '../ProcessFlowchartVizConstants';
import {convertFlowchartGqlToProcessInteractiveFlowchart} from './convertFlowchartGqlToProcessFlowchart';

type FlowchartConfigVersionOrInaccessibleGQL = NonNullable<
  NonNullable<
    ExtractByTypename<
      ProcessTaskSchemaEditorQuery['processTaskSchema'],
      'ProcessTaskSchemaNode'
    >['flowchartConfig']
  >['currentFlowchartConfigVersion']
>;

type FlowchartConfigVersionGQL = ExtractByTypename<
  FlowchartConfigVersionOrInaccessibleGQL,
  'ProcessFlowchartConfigVersionNode'
>;

type SchemaMapperConfigGql = FlowchartConfigVersionGQL['schemaMapperConfigs'][number];

interface HasId {
  id: string;
}

export interface ClientFlowchartConfig {
  availableTaskSchemas: Array<AvailableTaskSchema>;
  schemaMapperConfigs: Array<ClientSchemaMapperConfig>;
  templateFlowchart: ProcessInteractiveFlowchart | null;
  isFlowchartModifiableOnCreation: boolean;
  isFlowchartModifiableAfterCreation: boolean;
}

// The caller should ensure that everything is accessible. Here we simply filter out inaccessible data
function makeClientSchemaMapperConfig(
  schemaMapperConfig: SchemaMapperConfigGql,
  taskSchemasByTaskOrOutputSchemaId: {[id: string]: ProcessTaskSchemaWithoutFlowchartConfigGraphQL}
): ClientSchemaMapperConfig {
  const toTaskSchemaId: string | null = isInaccessible(schemaMapperConfig.toSchema)
    ? null
    : (schemaMapperConfig.toSchema as HasId).id;
  const accessibleFieldMapperConfigs = schemaMapperConfig.schemaFieldMapperConfigs.filter(
    (mapperConfig) => !isInaccessible(mapperConfig.fromSchema)
  );

  return {
    clientId: schemaMapperConfig.id,
    fromTaskSchemas: _.unique(
      accessibleFieldMapperConfigs,
      (fieldMapperConfig) => (fieldMapperConfig.fromSchema as HasId).id
    )
      .map(({fromSchema}) => taskSchemasByTaskOrOutputSchemaId[(fromSchema as HasId).id])
      .sort((fromTaskSchema1, fromTaskSchema2) => fromTaskSchema1.name.localeCompare(fromTaskSchema2.name)),
    toTaskSchema: toTaskSchemaId == null ? null : taskSchemasByTaskOrOutputSchemaId[toTaskSchemaId],
    mapperConfig:
      toTaskSchemaId == null
        ? null
        : {
            clientId: schemaMapperConfig.id,
            mapperType: schemaMapperConfig.type,
            toSchemaId: toTaskSchemaId,
            schemaFieldMapperConfigs: accessibleFieldMapperConfigs.map((fieldMapperConfig) => ({
              lookupConfig: {
                isMulti: fieldMapperConfig.lookupConfig.isMulti,
                lookupSteps: fieldMapperConfig.lookupConfig.lookupSteps,
              },
              toSchemaFieldId: fieldMapperConfig.toSchemaField.apiId,
              fromSchemaId: (fieldMapperConfig.fromSchema as HasId).id,
              resultMustBeNonNullToMap: fieldMapperConfig.resultMustBeNonNullToMap,
            })),
          },
  };
}

export function createAndSortClientSchemaMapperConfigs(
  taskSchemaByTaskOrOutputSchemaId: {[id: string]: ProcessTaskSchemaWithoutFlowchartConfigGraphQL},
  clientSchemaMapperConfigs: Array<SchemaMapperConfigGql>
): Array<ClientSchemaMapperConfig> {
  const schemaMapperConfigs = clientSchemaMapperConfigs
    .map((schemaMapperConfig) =>
      makeClientSchemaMapperConfig(schemaMapperConfig, taskSchemaByTaskOrOutputSchemaId)
    )
    .sort((schemaMapperConfig1, schemaMapperConfig2) => {
      const schemaMapperConfig1SortedFromTaskSchemaNames = schemaMapperConfig1.fromTaskSchemas
        .map((taskSchema) => taskSchema.name)
        .sort((fromTaskSchemaName1, fromTaskSchemaName2) =>
          fromTaskSchemaName1.localeCompare(fromTaskSchemaName2)
        );
      const schemaMapperConfig2SortedFromTaskSchemaNames = schemaMapperConfig2.fromTaskSchemas
        .map((taskSchema) => taskSchema.name)
        .sort((fromTaskSchemaName1, fromTaskSchemaName2) =>
          fromTaskSchemaName1.localeCompare(fromTaskSchemaName2)
        );
      let schemaMapperConfigToTaskComparison = 0;
      if (schemaMapperConfig1.toTaskSchema && schemaMapperConfig2.toTaskSchema) {
        schemaMapperConfigToTaskComparison = schemaMapperConfig1.toTaskSchema.name.localeCompare(
          schemaMapperConfig2.toTaskSchema.name
        );
      }
      return (
        schemaMapperConfig1SortedFromTaskSchemaNames
          .toString()
          .localeCompare(schemaMapperConfig2SortedFromTaskSchemaNames.toString()) ||
        schemaMapperConfigToTaskComparison
      );
    });
  return schemaMapperConfigs;
}

export function convertGQLFlowchartConfigToClient(
  flowchartConfig: FlowchartConfigVersionOrInaccessibleGQL | null,
  allNodeSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>,
  rootTaskSchema: ProcessTaskSchemaGraphQL
): ClientFlowchartConfig {
  if (flowchartConfig == null || isInaccessible(flowchartConfig)) {
    return {
      availableTaskSchemas: [],
      schemaMapperConfigs: [],
      templateFlowchart: null,
      isFlowchartModifiableOnCreation: IS_FLOWCHART_MODIFIABLE_ON_CREATION_DEFAULT,
      isFlowchartModifiableAfterCreation: IS_FLOWCHART_MODIFIABLE_AFTER_CREATION_DEFAULT,
    };
  }

  const allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL> = [
    ...allNodeSchemas,
    rootTaskSchema,
  ];
  const taskSchemaByTaskOrOutputSchemaId: {[id: string]: ProcessTaskSchemaWithoutFlowchartConfigGraphQL} = {};
  for (const schema of allTaskSchemas) {
    taskSchemaByTaskOrOutputSchemaId[schema.id] = schema;
    if (schema.outputSchema != null) {
      taskSchemaByTaskOrOutputSchemaId[schema.outputSchema.id] = schema;
    }
  }

  const availableTaskSchemas = accessibleObjs(flowchartConfig.availableSchemas).map(
    ({id}) => taskSchemaByTaskOrOutputSchemaId[id]
  );

  const schemaMapperConfigs = createAndSortClientSchemaMapperConfigs(
    taskSchemaByTaskOrOutputSchemaId,
    flowchartConfig.schemaMapperConfigs
  );

  let templateFlowchart: ProcessInteractiveFlowchart | null = null;
  if (flowchartConfig.templateFlowchart != null) {
    templateFlowchart = convertFlowchartGqlToProcessInteractiveFlowchart(flowchartConfig.templateFlowchart);
  }
  if (templateFlowchart != null && templateFlowchart?.clientId == null) {
    templateFlowchart.clientId = uuidv4();
  }
  const isFlowchartModifiableOnCreation = flowchartConfig.isFlowchartModifiableOnCreation;
  const isFlowchartModifiableAfterCreation = flowchartConfig.isFlowchartModifiableAfterCreation;

  return {
    availableTaskSchemas,
    schemaMapperConfigs,
    templateFlowchart,
    isFlowchartModifiableOnCreation,
    isFlowchartModifiableAfterCreation,
  };
}
